import { Section, Sub, Code } from '../../../components/docs/Blocks';
import { siteConfig } from '../../../data/siteConfig';

const faqs = [
  {
    q: 'Where are my Minecraft Bedrock worlds stored?',
    a: (
      <>
        Since Minecraft moved to the GDK version, worlds live under{' '}
        <Code>%appdata%\Minecraft Bedrock\Users\&lt;user id&gt;\games\com.mojang\minecraftWorlds</Code>,
        one folder per world. You should not need to find that yourself — the picker
        checks every standard location for the Store version, the standalone launcher,
        Preview and Education Edition, and groups what it finds by install. If your
        worlds live somewhere else, use <strong>Browse for level.dat</strong> and the
        folder is remembered.
      </>
    ),
  },
  {
    q: 'Does it run on Linux?',
    a: (
      <>
        Yes, since 3.0.3. The Linux build is a 64-bit x86 archive with the .NET
        runtime inside, so there is nothing to install: extract it and run{' '}
        <Code>./BedrockToolchest</Code>. It is validated on Ubuntu 24.04; other
        distributions should work but have not been tried, and ARM machines are not
        supported. The picker looks for mcpelauncher worlds under{' '}
        <Code>~/.local/share/mcpelauncher</Code> and <Code>~/mcpelauncher_linux</Code>;
        anything else, use <strong>Browse for level.dat</strong>. Keep worlds on a
        local Linux filesystem rather than a Windows mount or a shared folder.
      </>
    ),
  },
  {
    q: 'Can I edit worlds from Xbox, PlayStation or Switch?',
    a: 'Through a Realm, yes. Since 3.1, Toolchest can download a Realm you own, edit it and upload it back, so a console world you have put on a Realm can be edited from the PC and played on the console again. Otherwise a console world has to reach a PC first, by a USB export where the console supports one.',
  },
  {
    q: 'Is it safe to edit a world while Minecraft has it open?',
    a: 'No, and Toolchest will not let you. It takes the world\'s database lock when it opens, so if Minecraft, a server or another copy of Toolchest is holding the world, it says which one and refuses. Returning to the Minecraft main menu is enough to release a world — you do not have to quit the game.',
  },
  {
    q: 'My changes are not showing up in Minecraft.',
    a: 'Two things to check. First, that you pressed Save Changes — Toolchest stages everything and writes nothing until you do. Second, that Minecraft actually reloaded the world: leaving to the main menu and re-entering is enough, but Minecraft must have closed the world after Toolchest finished with it. Toolchest reminds you after a save that it is still holding the world open, and offers to close it.',
  },
  {
    q: 'Can I undo changes after saving?',
    a: 'Yes. Restore the backup from the Backups screen — with the default settings, one was taken before the first save of that session. Restoring takes a backup of the current state first, so the restore itself can be undone.',
  },
  {
    q: 'Why does a world take a while to open the first time I use a screen?',
    a: 'Several screens — the World Map, Entities, Block Entities, Villages, Villager Trades and Tick Data — answer their question by reading every record in the world database. On a large world that is millions of records. Each screen reports progress while it works and caches the result for the session, and no screen does that work until you open it. Block Search is the exception to the caching: each search is its own pass, and a coordinate range narrows it.',
  },
  {
    q: 'Why do players show as "Player #3" instead of gamertags?',
    a: 'Because Minecraft Bedrock does not store gamertags in player records — the world genuinely does not know. Toolchest reads a name from an add-on that captures it, from a name you type yourself, or from a PlayerName tag if the record happens to carry one. Typing a name is stored beside the world rather than inside it, and if the name is a real gamertag the head and 3D model are fetched to match.',
  },
  {
    q: 'Does this work with Java Edition worlds?',
    a: 'No. Java Edition uses a completely different world format. Toolchest is Bedrock only.',
  },
  {
    q: 'Does it need an internet connection?',
    a: 'Not for worlds on your computer. The only requests it makes for those are the update check and, if you leave it on, player head and 3D model previews. Both can be turned off, and everything else works offline. Realms & Servers needs a connection, of course, and makes no requests until you use it.',
  },
  {
    q: 'Is it free?',
    a: 'Yes, and it stays free. No paywalls, no subscriptions, no accounts. You can use it for commercial work too, such as building Marketplace maps. The app itself just can\'t be resold.',
  },
];

const issues = [
  {
    title: 'The program will not start',
    body: 'Check you are on 64-bit Windows 10 or later. Windows SmartScreen may warn about an unrecognised publisher the first time — the executable is not code-signed. If your antivirus quarantines it, add an exception for the .exe.',
  },
  {
    title: 'The program will not start on Linux',
    body: 'Launch it from a terminal so you can see why. A missing-library message means a system package is absent: the README in the archive lists the Ubuntu 24.04 packages the program and its embedded 3D viewers need, and other distributions have their own names for the same libraries. A "permission denied" means the extractor dropped the executable bit — chmod +x BedrockToolchest. Bring the terminal output to Discord if neither is it.',
  },
  {
    title: '"This world is open somewhere else"',
    body: 'Minecraft, a Bedrock server, or another copy of Toolchest is holding the world. The message names which. Close it there and press Refresh. If nothing obvious is holding it, look for a lingering Minecraft process in Task Manager. On Linux the check can only see visible programs using the world, so keep the game and any server closed while you edit rather than relying on it.',
  },
  {
    title: '"Database needs repair" in the world list',
    body: 'The db/CURRENT pointer file is damaged — 0 bytes, or filled with something that is not a MANIFEST pointer. Minecraft cannot open the world either, and the world data behind it is usually intact. Toolchest cannot repair this yet; restore the world from a backup if you have one.',
  },
  {
    title: 'Saving takes a while on a large world',
    body: 'The backup is normally the slowest part, and it happens once per session rather than once per save. A 600 MB world backs up in a few seconds including a full verify pass; a 3 GB one takes longer. If you do not want it, set Back up before saving to Ask or Never in Settings.',
  },
  {
    title: 'The world map shows nothing for a dimension',
    body: 'Nothing has been generated there. A world where nobody has visited the End has no End chunks to draw, and the map says so rather than showing an empty grid.',
  },
  {
    title: 'A village has stopped breeding or restocking',
    body: 'Open Villages and look at its Dwellers and Claims. A dweller that names an entity no longer in the world still counts toward the village population, and a workstation claimed by a villager the village no longer lists is never freed. Both are flagged, and the orphaned claims can be released.',
  },
  {
    title: 'Something went wrong and you want to report it',
    body: 'Open the Console, press Save Log and attach the file. Toolchest writes a log file per session, so the log from a session that crashed is still on disk — it offers it on the next launch.',
  },
];

export default function Troubleshooting() {
  return (
    <Section id="troubleshooting" title="Troubleshooting & FAQ">
      <Sub id="faq" title="Frequently asked">
        <div className="space-y-4">
          {faqs.map(({ q, a }) => (
            <div
              key={q}
              className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5"
            >
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">{q}</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{a}</p>
            </div>
          ))}
        </div>
      </Sub>

      <Sub id="common-issues" title="Common issues">
        <div className="space-y-4">
          {issues.map(({ title, body }) => (
            <div
              key={title}
              className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-5"
            >
              <h4 className="font-semibold text-slate-900 dark:text-white mb-2">{title}</h4>
              <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </Sub>

      <div className="rounded-xl border border-primary-200 dark:border-primary-800/50 bg-primary-50 dark:bg-primary-900/20 p-5">
        <h3 className="font-semibold text-primary-800 dark:text-primary-300 mb-2">
          Need more help?
        </h3>
        <p className="text-sm text-primary-700 dark:text-primary-400">
          Join the{' '}
          <a
            href={siteConfig.discordUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-primary-600 dark:hover:text-primary-300"
          >
            Discord server
          </a>{' '}
          to ask questions, report bugs or suggest features. Bring the log file if
          something went wrong — it usually answers the question in one step.
        </p>
      </div>
    </Section>
  );
}
